import { useState, useEffect, useRef } from 'react';
import styles from './MatchingBlock.module.css';
import { useAssistant } from '../../../components/Assistant/AssistantContext'; 

const MatchingBlock = ({ block, previousScore, onComplete }) => {
  const [selectedNative, setSelectedNative] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [score, setScore] = useState(previousScore);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [bonusPoints, setBonusPoints] = useState(3); // Начальные бонусные очки
  const [incorrectTarget, setIncorrectTarget] = useState(null);
  const [currentWords, setCurrentWords] = useState([]);
  const [wordIndex, setWordIndex] = useState(0); // Для отслеживания текущего набора слов
  const timerRef = useRef(null);
  const { actions, state } = useAssistant();

  // Формируем начальный список слов из block.words
  const initialWords = block.words.map((word) => ({
    native: word.word,
    target: word.translation,
  }));

  // Формируем список usedVerbsAdjective
  const verbsAdjectives = block.usedVerbsAdjective.map((item) => ({
    native: item.word,
    target: item.translation,
  }));

  // Функция для получения следующего набора слов с фильтрацией по одинаковым словам
	const getNextWords = () => {
	  actions.showSuccess(); 
	  const filterDuplicates = (words) => {
		const seenNative = new Set();
		const seenTarget = new Set();
		return words.filter((pair) => {
		  if (seenNative.has(pair.native) || seenTarget.has(pair.target)) return false;
		  seenNative.add(pair.native);
		  seenTarget.add(pair.target);
		  return true;
		});
	  };

	  if (wordIndex < initialWords.length) {
		const rawSlice = initialWords.slice(wordIndex, wordIndex + 5);
		return filterDuplicates(rawSlice);
	  } else {
		const startIdx = wordIndex - initialWords.length;
		const mainSlice = verbsAdjectives.slice(startIdx, startIdx + 5);
		const filteredMain = filterDuplicates(mainSlice);

		if (filteredMain.length < 5 && filteredMain.length > 0) {
		  const alreadyUsed = new Set(filteredMain.map((w) => w.native + "|" + w.target));
		  const availableWords = verbsAdjectives.filter(
			(word) => !alreadyUsed.has(word.native + "|" + word.target)
		  );

		  const fillerWords = filterDuplicates(
			availableWords.sort(() => Math.random() - 0.5)
		  ).slice(0, 5 - filteredMain.length);

		  return [...filteredMain, ...fillerWords];
		}

		return filteredMain;
	  }
	};

  

  // Инициализация текущих слов
  useEffect(() => {
    setCurrentWords(getNextWords());
  }, [wordIndex]);

  // Перемешивание слов
  const [shuffledNative, setShuffledNative] = useState([]);
  const [shuffledTarget, setShuffledTarget] = useState([]);

  useEffect(() => {
    setShuffledNative([...currentWords].sort(() => Math.random() - 0.5).map((w) => w.native));
    setShuffledTarget([...currentWords].sort(() => Math.random() - 0.5).map((w) => w.target));
  }, [currentWords]);

  // Таймер и логика бонусных очков
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeElapsed((prev) => {
        const newTime = prev + 1;
        if (newTime <= 10) {
          setBonusPoints(3);
        } else if (newTime <= 20) {
          setBonusPoints(2);
        } else if (newTime <= 30) {
          setBonusPoints(1);
        } else {
          setBonusPoints(0);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, []);

  const handleSelect = (word, type) => {
    if (type === 'native') {
      setSelectedNative(word);
      setIncorrectTarget(null);
    } else if (selectedNative) {
      const pair = currentWords.find((w) => w.native === selectedNative && w.target === word);
      if (pair) {
        setMatchedPairs([...matchedPairs, pair]);
        setSelectedNative(null);
      } else {
		actions.showError();  
        setIncorrectTarget(word);
        setTimeout(() => setIncorrectTarget(null), 1000);
        setTimeout(() => setSelectedNative(null), 100);
      }

      // Проверка завершения текущего набора слов
      if (matchedPairs.length + (pair ? 1 : 0) === currentWords.length) {
        setScore((prev) => prev + 5 + bonusPoints); // Добавляем базовые очки + бонус  
        if (
          wordIndex + currentWords.length >= initialWords.length + verbsAdjectives.length ||
          currentWords.length === 0
        ) {
          // Игра завершена
          setTimeout(() => onComplete(score), 1000); // Передаем итоговый счет
        } else {
          // Переход к следующему набору слов
          clearInterval(timerRef.current);
          setTimeElapsed(0); // Сбрасываем время
          setBonusPoints(3); // Сбрасываем бонусные очки
          setMatchedPairs([]);
          setWordIndex((prev) => prev + currentWords.length);
          // Перезапускаем таймер
          timerRef.current = setInterval(() => {
            setTimeElapsed((prev) => {
              const newTime = prev + 1;
              if (newTime <= 10) {
                setBonusPoints(3);
              } else if (newTime <= 20) {
                setBonusPoints(2);
              } else if (newTime <= 30) {
                setBonusPoints(1);
              } else {
                setBonusPoints(0);
              }
              return newTime;
            });
          }, 1000);
        }
      }
    }
  };

  return (
    <>
      <p className={styles.score}>
		  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="gold" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
			<path d="M12 .587l3.668 7.568L24 9.423l-6 5.885 1.417 8.255L12 18.897l-7.417 4.666L6 15.308 0 9.423l8.332-1.268z" />
		  </svg>
		  {score}
		</p>
      <p className={styles.timer}>Time: {timeElapsed} (Bonus: +{bonusPoints}) </p>
      <div className={styles.grid}>
        <div className={styles.column}>
          {shuffledNative.map((word, idx) => (
            <div key={idx} className={styles.buttonWrapper}>
              <button
                onClick={() => handleSelect(word, 'native')}
                disabled={matchedPairs.some((pair) => pair.native === word)}
                className={`${styles.button} ${
                  selectedNative === word && !incorrectTarget
                    ? styles.selected
                    : matchedPairs.some((pair) => pair.native === word)
                    ? styles.disabled
                    : ''
                }`}
              >
                {word}
              </button>
              {matchedPairs.some((pair) => pair.native === word) && (
                <span className={styles.checkmark}>✔</span>
              )}
            </div>
          ))}
        </div>
        <div className={styles.column}>
          {shuffledTarget.map((word, idx) => (
            <div key={idx} className={styles.buttonWrapper}>
              <button
                onClick={() => handleSelect(word, 'target')}
                disabled={matchedPairs.some((pair) => pair.target === word)}
                className={`${styles.button} ${
                  incorrectTarget === word
                    ? styles.incorrect
                    : matchedPairs.some((pair) => pair.target === word)
                    ? styles.disabled
                    : ''
                }`}
              >
                {word}
              </button>
              {matchedPairs.some((pair) => pair.target === word) && (
                <span className={styles.checkmark}>✔</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default MatchingBlock;
